import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import {
  COMMUNITY_CATEGORIES,
  addCommunityComment,
  addCommunityLinkAttachment,
  buildCommunityAssetUrl,
  createCommunityThread,
  fetchCommunityStrategy,
  fetchCommunityThread,
  importCommunityStrategy,
  listCommunityThreads,
  uploadCommunityAttachments,
} from './communityDataFeeds';
import {
  fetchTesterUserProfile,
  listSavedTesterStrategies,
} from './strategyTesterDataFeeds';
import './CommunityPage.css';

const DEFAULT_DRAFT = {
  title: '',
  body: '',
  category: 'strategy',
  tags: '',
  strategyId: '',
  linkTitle: '',
  linkUrl: '',
  linkType: 'link',
};

function formatDate(value) {
  if (!value) return '';
  return new Date(value).toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatModelingMode(value = '') {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function safeFileName(value, fallback = 'strategy') {
  return String(value || fallback)
    .replace(/[^\w.-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '') || fallback;
}

function downloadTextFile(filename, content) {
  const blob = new Blob([content], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

// Convert a YouTube/Vimeo watch URL into an embeddable player URL so videos play inline.
function getVideoEmbedUrl(rawUrl) {
  if (!rawUrl) return '';
  try {
    const url = new URL(rawUrl);
    const host = url.hostname.replace(/^www\./, '');
    if (host === 'youtu.be') {
      const id = url.pathname.slice(1);
      return id ? `https://www.youtube.com/embed/${id}` : '';
    }
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (url.pathname.startsWith('/shorts/')) {
        const id = url.pathname.split('/')[2];
        return id ? `https://www.youtube.com/embed/${id}` : '';
      }
      if (url.pathname === '/watch') {
        const id = url.searchParams.get('v');
        return id ? `https://www.youtube.com/embed/${id}` : '';
      }
      if (url.pathname.startsWith('/embed/')) return rawUrl;
    }
    if (host === 'vimeo.com') {
      const id = url.pathname.split('/').filter(Boolean)[0];
      return id && /^\d+$/.test(id) ? `https://player.vimeo.com/video/${id}` : '';
    }
  } catch (error) {
    return '';
  }
  return '';
}

function AttachmentPreview({ attachment }) {
  const fileUrl = attachment.filePath ? buildCommunityAssetUrl(attachment.filePath) : '';
  const href = attachment.url || fileUrl;
  const title = attachment.title || attachment.originalName || 'Attachment';

  if (!href) {
    return null;
  }

  if (attachment.type === 'image' && fileUrl) {
    return (
      <a className="community-attachment image" href={fileUrl} target="_blank" rel="noopener noreferrer">
        <img src={fileUrl} alt={title} loading="lazy" />
        <span>{title}</span>
      </a>
    );
  }

  if (attachment.type === 'video' && fileUrl) {
    return (
      <div className="community-attachment video">
        <video src={fileUrl} controls preload="metadata" />
        <a href={fileUrl} target="_blank" rel="noopener noreferrer">{title}</a>
      </div>
    );
  }

  const embedUrl = getVideoEmbedUrl(href);
  if (embedUrl) {
    return (
      <div className="community-attachment video-embed">
        <div className="community-video-frame">
          <iframe
            src={embedUrl}
            title={title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
        <a href={href} target="_blank" rel="noopener noreferrer">{title}</a>
      </div>
    );
  }

  return (
    <a className={`community-attachment ${attachment.type || 'file'}`} href={href} target="_blank" rel="noopener noreferrer">
      <span className="community-attachment-type">{attachment.type || 'file'}</span>
      <strong>{title}</strong>
      {attachment.size ? <small>{Math.ceil(attachment.size / 1024)} KB</small> : null}
    </a>
  );
}

function CommunityPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('userToken') || '';
  const [profile, setProfile] = useState(null);
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [savedStrategies, setSavedStrategies] = useState([]);
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState(DEFAULT_DRAFT);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [commentDraft, setCommentDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [threadLoading, setThreadLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const isSignedIn = Boolean(token);
  const hasVixPremium = profile?.testerPlan === 'premium' || !!profile?.canExportMq5;
  const selectedStrategy = useMemo(
    () => savedStrategies.find((strategy) => String(strategy.id) === String(draft.strategyId)),
    [draft.strategyId, savedStrategies],
  );

  const loadThreads = useCallback(async (options = {}) => {
    if (!token) {
      setLoading(false);
      return;
    }
    const nextCategory = options.category ?? category;
    const nextSearch = options.search ?? search;
    const response = await listCommunityThreads({
      category: nextCategory,
      search: nextSearch,
      token,
    });
    setThreads(Array.isArray(response.threads) ? response.threads : []);
  }, [category, search, token]);

  const loadThreadDetail = useCallback(async (threadId) => {
    if (!threadId || !token) return;
    setThreadLoading(true);
    setError('');
    try {
      const response = await fetchCommunityThread(threadId, token);
      setActiveThread(response.thread || null);
    } catch (loadError) {
      setError(loadError.message || 'Failed to load thread.');
    } finally {
      setThreadLoading(false);
    }
  }, [token]);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      if (!token) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const profileResponse = await fetchTesterUserProfile(token);
        if (cancelled) return;
        setProfile(profileResponse.user || null);
        const premium = profileResponse.user?.testerPlan === 'premium' || !!profileResponse.user?.canExportMq5;
        const threadResponse = await listCommunityThreads({ category: '', search: '', token });
        const strategyResponse = premium ? await listSavedTesterStrategies(token) : { strategies: [] };
        if (cancelled) return;
        const nextThreads = Array.isArray(threadResponse.threads) ? threadResponse.threads : [];
        setThreads(nextThreads);
        setSavedStrategies(Array.isArray(strategyResponse.strategies) ? strategyResponse.strategies : []);
        if (nextThreads[0]) {
          await loadThreadDetail(nextThreads[0].id);
        }
      } catch (bootstrapError) {
        if (!cancelled) {
          setError(bootstrapError.message || 'Failed to load community.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [loadThreadDetail, token]);

  const handleFilterSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      await loadThreads();
      setActiveThread(null);
    } catch (filterError) {
      setError(filterError.message || 'Failed to filter community threads.');
    } finally {
      setLoading(false);
    }
  };

  const handleDraftChange = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const handleCreateThread = async (event) => {
    event.preventDefault();
    if (!token) return;
    if (!hasVixPremium) {
      setError('VixPremium is required to post community threads.');
      return;
    }
    setPosting(true);
    setError('');
    setNotice('');
    try {
      const attachments = draft.linkUrl
        ? [{
          title: draft.linkTitle,
          url: draft.linkUrl,
          type: draft.linkType,
        }]
        : [];
      const response = await createCommunityThread({
        title: draft.title,
        body: draft.body,
        category: draft.category,
        tags: draft.tags,
        strategyId: draft.strategyId,
        attachments,
      }, token);
      let nextThread = response.thread;
      if (nextThread?.id && selectedFiles.length) {
        await uploadCommunityAttachments(nextThread.id, selectedFiles, token);
        const refreshed = await fetchCommunityThread(nextThread.id, token);
        nextThread = refreshed.thread;
      }
      setDraft(DEFAULT_DRAFT);
      setSelectedFiles([]);
      setShowCompose(false);
      setNotice('Thread published to VixPremium Community.');
      await loadThreads({ category: '', search: '' });
      setCategory('');
      setSearch('');
      if (nextThread?.id) {
        setActiveThread(nextThread);
      }
    } catch (createError) {
      setError(createError.message || 'Failed to create thread.');
    } finally {
      setPosting(false);
    }
  };

  const handleAddComment = async (event) => {
    event.preventDefault();
    if (!activeThread?.id || !commentDraft.trim()) return;
    if (!hasVixPremium) {
      setError('VixPremium is required to comment in the community.');
      return;
    }
    setPosting(true);
    setError('');
    try {
      await addCommunityComment(activeThread.id, commentDraft, token);
      setCommentDraft('');
      await loadThreadDetail(activeThread.id);
      await loadThreads();
    } catch (commentError) {
      setError(commentError.message || 'Failed to post comment.');
    } finally {
      setPosting(false);
    }
  };

  const handleAddLink = async () => {
    if (!activeThread?.id || !draft.linkUrl) return;
    if (!hasVixPremium) {
      setError('VixPremium is required to attach community links.');
      return;
    }
    setPosting(true);
    setError('');
    try {
      await addCommunityLinkAttachment(activeThread.id, {
        title: draft.linkTitle,
        url: draft.linkUrl,
        type: draft.linkType,
      }, token);
      setDraft((current) => ({ ...current, linkTitle: '', linkUrl: '', linkType: 'link' }));
      await loadThreadDetail(activeThread.id);
    } catch (linkError) {
      setError(linkError.message || 'Failed to attach link.');
    } finally {
      setPosting(false);
    }
  };

  const handleImportStrategy = async () => {
    if (!activeThread?.id) return;
    if (!hasVixPremium) {
      setError('VixPremium is required to import community strategies.');
      return;
    }
    setPosting(true);
    setError('');
    try {
      const response = await importCommunityStrategy(activeThread.id, token);
      if (response.strategy?.id) {
        navigate(`/tester?strategy=${response.strategy.id}`);
      }
    } catch (importError) {
      setError(importError.message || 'Failed to import strategy.');
    } finally {
      setPosting(false);
    }
  };

  const handleDownloadStrategy = async () => {
    if (!activeThread?.id) return;
    if (!hasVixPremium) {
      setError('VixPremium is required to download community strategies.');
      return;
    }
    setPosting(true);
    setError('');
    try {
      const response = await fetchCommunityStrategy(activeThread.id, token);
      const strategy = response.strategy;
      downloadTextFile(`${safeFileName(strategy?.name || activeThread.title)}.json`, JSON.stringify(strategy, null, 2));
      await loadThreadDetail(activeThread.id);
      await loadThreads();
    } catch (downloadError) {
      setError(downloadError.message || 'Failed to download strategy.');
    } finally {
      setPosting(false);
    }
  };

  return (
    <HelmetProvider>
      <div className="community-page page-shell">
        <Helmet>
          <title>VixPremium Community - VixAlgoBuilder</title>
          <meta
            name="description"
            content="Share strategies, reports, images, files, and trading ideas with VixPremium members."
          />
        </Helmet>

        <section className="community-hero">
          <div>
            <span className="tag">VixPremium Community</span>
            <h1 className="page-title">Share, test, import, and improve trading strategies together.</h1>
            <p className="page-lead">
              A premium workspace for strategy discussions, downloadable EA ideas, MT5 reports, files,
              and public builder presets that members can import into VixAlgoBuilder.
            </p>
            <div className="community-hero-actions">
              <Link to="/tester" className="primary-cta">Open Builder</Link>
              <Link to="/builder-docs" className="ghost-cta">Read Documentation</Link>
              {!hasVixPremium ? <Link to="/tester-premium" className="ghost-cta">Activate VixPremium</Link> : null}
            </div>
          </div>
          <div className="community-hero-panel">
            <div>
              <strong>{threads.length}</strong>
              <span>active threads loaded</span>
            </div>
            <div>
              <strong>{savedStrategies.length}</strong>
              <span>vault strategies available to share</span>
            </div>
            <div>
              <strong>Builder</strong>
              <span>strategy snapshots stay builder-ready</span>
            </div>
          </div>
        </section>

        {!isSignedIn && (
          <section className="community-gate section-card">
            <h2>Sign in to access the community.</h2>
            <p className="muted-text">
              Sign in to read community threads. VixPremium is required to post, comment, download, or import shared strategies.
            </p>
            <div className="community-hero-actions">
              <Link to="/login" className="primary-cta">Sign In</Link>
              <Link to="/signup" className="ghost-cta">Create Account</Link>
            </div>
          </section>
        )}

        {isSignedIn && !loading && !hasVixPremium && (
          <section className="community-gate section-card">
            <h2>Read-only community access.</h2>
            <p className="muted-text">
              You can read community threads on the free plan. VixPremium is required to post, comment,
              download shared strategies, or import them into your vault.
            </p>
            <Link to="/tester-premium" className="primary-cta">View VixPremium</Link>
          </section>
        )}

        {isSignedIn && (
          <section className="community-layout">
            <aside className="community-sidebar section-card">
              <form className="community-filter" onSubmit={handleFilterSubmit}>
                <label>
                  Search community
                  <input
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Donchian, breakout, report..."
                  />
                </label>
                <label>
                  Category
                  <select value={category} onChange={(event) => setCategory(event.target.value)}>
                    {COMMUNITY_CATEGORIES.map((item) => (
                      <option key={item.id || 'all'} value={item.id}>{item.label}</option>
                    ))}
                  </select>
                </label>
                <button type="submit" className="ghost-button compact-button" disabled={loading}>
                  {loading ? 'Loading...' : 'Filter'}
                </button>
              </form>

              <div className="community-thread-list">
                {threads.length === 0 ? (
                  <p className="muted-text">No community threads match this filter yet.</p>
                ) : threads.map((thread) => (
                  <button
                    key={thread.id}
                    type="button"
                    className={`community-thread-card ${activeThread?.id === thread.id ? 'active' : ''}`}
                    onClick={() => loadThreadDetail(thread.id)}
                  >
                    <span className="community-category">{thread.category}</span>
                    <strong>{thread.title}</strong>
                    <small>
                      {thread.symbol || 'Strategy'} {thread.timeframe ? `· ${thread.timeframe}` : ''} · {thread.commentCount} comments
                    </small>
                  </button>
                ))}
              </div>
            </aside>

            <main className="community-main">
              {error ? <div className="section-card error-card">{error}</div> : null}
              {notice ? <div className="section-card success-card">{notice}</div> : null}

              {hasVixPremium ? (
                <div className="section-card community-main-toolbar">
                  <div>
                    <h2>Community threads</h2>
                    <p className="muted-text">
                      Share an idea, attach proof, or publish a builder strategy snapshot for others to import.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="primary-cta compact-button"
                    onClick={() => setShowCompose((value) => !value)}
                  >
                    {showCompose ? 'Close composer' : '+ New thread'}
                  </button>
                </div>
              ) : null}

              {hasVixPremium && showCompose ? (
              <section className="section-card community-compose">
                <div className="community-section-heading">
                  <div>
                    <h2>Start a community thread</h2>
                    <p className="muted-text">
                      Share an idea, attach proof, or publish a builder strategy snapshot for others to import.
                    </p>
                  </div>
                  <Link to="/dashboard?tab=strategies" className="ghost-button compact-button">Open Vault</Link>
                </div>

                <form onSubmit={handleCreateThread} className="community-form">
                  <div className="community-form-grid">
                    <label>
                      Title
                      <input
                        value={draft.title}
                        onChange={(event) => handleDraftChange('title', event.target.value)}
                        placeholder="Donchian breakout with conservative exits"
                        required
                      />
                    </label>
                    <label>
                      Category
                      <select value={draft.category} onChange={(event) => handleDraftChange('category', event.target.value)}>
                        {COMMUNITY_CATEGORIES.filter((item) => item.id).map((item) => (
                          <option key={item.id} value={item.id}>{item.label}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Share strategy from vault
                      <select value={draft.strategyId} onChange={(event) => handleDraftChange('strategyId', event.target.value)}>
                        <option value="">No strategy attached</option>
                        {savedStrategies.map((strategy) => (
                          <option key={strategy.id} value={strategy.id}>
                            {strategy.name} {strategy.symbol ? `- ${strategy.symbol} ${strategy.timeframe}` : ''}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Tags
                      <input
                        value={draft.tags}
                        onChange={(event) => handleDraftChange('tags', event.target.value)}
                        placeholder="breakout, low risk"
                      />
                    </label>
                  </div>
                  {selectedStrategy ? (
                    <p className="community-selected-strategy">
                      Sharing: <strong>{selectedStrategy.name}</strong> · {selectedStrategy.symbol || '-'} {selectedStrategy.timeframe || ''}
                    </p>
                  ) : null}
                  <label>
                    Thread details
                    <textarea
                      value={draft.body}
                      onChange={(event) => handleDraftChange('body', event.target.value)}
                      placeholder="Explain the logic, settings, what worked, what failed, and what you want feedback on."
                      rows={5}
                    />
                  </label>
                  <div className="community-form-grid">
                    <label>
                      Link title
                      <input
                        value={draft.linkTitle}
                        onChange={(event) => handleDraftChange('linkTitle', event.target.value)}
                        placeholder="MT5 report, YouTube explanation, broker notes..."
                      />
                    </label>
                    <label>
                      Link URL
                      <input
                        value={draft.linkUrl}
                        onChange={(event) => handleDraftChange('linkUrl', event.target.value)}
                        placeholder="https://..."
                      />
                    </label>
                    <label>
                      Link type
                      <select value={draft.linkType} onChange={(event) => handleDraftChange('linkType', event.target.value)}>
                        <option value="link">Link</option>
                        <option value="report">MT5 report</option>
                        <option value="video">Video</option>
                        <option value="pdf">PDF</option>
                      </select>
                    </label>
                    <label>
                      Upload files
                      <input
                        type="file"
                        multiple
                        onChange={(event) => setSelectedFiles(Array.from(event.target.files || []))}
                        accept=".png,.jpg,.jpeg,.webp,.gif,.pdf,.mp4,.mov,.webm,.html,.htm,.mq5,.mqh,.set,.zip,.txt,.csv"
                      />
                    </label>
                  </div>
                  {selectedFiles.length ? (
                    <p className="muted-text">{selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected for upload.</p>
                  ) : null}
                  <button type="submit" className="primary-cta compact-button" disabled={posting}>
                    {posting ? 'Publishing...' : 'Publish Thread'}
                  </button>
                </form>
              </section>
              ) : null}

              <section className="section-card community-detail">
                {threadLoading ? <p className="muted-text">Loading thread...</p> : null}
                {!threadLoading && !activeThread ? (
                  <p className="muted-text">Select a community thread to inspect strategy details, attachments, and discussion.</p>
                ) : null}
                {activeThread ? (
                  <>
                    <div className="community-detail-header">
                      <div>
                        <span className="community-category">{activeThread.category}</span>
                        <h2>{activeThread.title}</h2>
                        <p className="muted-text">
                          By {activeThread.author?.name || 'Member'} · {formatDate(activeThread.createdAt)}
                        </p>
                      </div>
                      <div className="community-thread-metrics">
                        <span>{activeThread.viewCount} views</span>
                        <span>{activeThread.downloadCount} downloads</span>
                        <span>{activeThread.importCount} imports</span>
                      </div>
                    </div>

                    {activeThread.tags?.length ? (
                      <div className="community-tags">
                        {activeThread.tags.map((tag) => <span key={tag}>{tag}</span>)}
                      </div>
                    ) : null}

                    <p className="community-body">{activeThread.body || 'No description provided.'}</p>

                    {activeThread.strategy ? (
                      <div className="community-strategy-box">
                        <div>
                          <h3>{activeThread.strategy.name}</h3>
                          <p className="muted-text">
                            {activeThread.strategy.symbol || activeThread.symbol || '-'} · {activeThread.strategy.timeframe || activeThread.timeframe || '-'}
                            {activeThread.strategy.setup?.modelingMode ? ` · ${formatModelingMode(activeThread.strategy.setup.modelingMode)}` : ''}
                          </p>
                        </div>
                        <div className="community-hero-actions">
                          <button type="button" className="primary-cta compact-button" onClick={handleImportStrategy} disabled={posting || !hasVixPremium}>
                            Import to Builder
                          </button>
                          <button type="button" className="ghost-button compact-button" onClick={handleDownloadStrategy} disabled={posting || !hasVixPremium}>
                            Download JSON
                          </button>
                        </div>
                        {!hasVixPremium ? <p className="muted-text">Upgrade to VixPremium to download or import shared strategies.</p> : null}
                      </div>
                    ) : null}

                    {activeThread.attachments?.length ? (
                      <div className="community-attachments">
                        <h3>Shared files and links</h3>
                        <div className="community-attachment-grid">
                          {activeThread.attachments.map((attachment) => (
                            <AttachmentPreview key={attachment.id} attachment={attachment} />
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {hasVixPremium ? (
                    <div className="community-add-link">
                      <h3>Add another link</h3>
                      <div className="community-form-grid">
                        <input
                          value={draft.linkTitle}
                          onChange={(event) => handleDraftChange('linkTitle', event.target.value)}
                          placeholder="Link title"
                        />
                        <input
                          value={draft.linkUrl}
                          onChange={(event) => handleDraftChange('linkUrl', event.target.value)}
                          placeholder="https://..."
                        />
                        <select value={draft.linkType} onChange={(event) => handleDraftChange('linkType', event.target.value)}>
                          <option value="link">Link</option>
                          <option value="report">MT5 report</option>
                          <option value="video">Video</option>
                          <option value="pdf">PDF</option>
                        </select>
                        <button type="button" className="ghost-button compact-button" onClick={handleAddLink} disabled={posting || !draft.linkUrl}>
                          Attach Link
                        </button>
                      </div>
                    </div>
                    ) : null}

                    <div className="community-comments">
                      <h3>Discussion</h3>
                      {activeThread.comments?.length ? activeThread.comments.map((comment) => (
                        <article key={comment.id} className="community-comment">
                          <div>
                            <strong>{comment.author?.name || 'Member'}</strong>
                            <small>{formatDate(comment.createdAt)}</small>
                          </div>
                          <p>{comment.body}</p>
                        </article>
                      )) : <p className="muted-text">No comments yet.</p>}
                      {hasVixPremium ? (
                      <form onSubmit={handleAddComment} className="community-comment-form">
                        <textarea
                          value={commentDraft}
                          onChange={(event) => setCommentDraft(event.target.value)}
                          rows={3}
                          placeholder="Ask a question, suggest a setting change, or share test results."
                        />
                        <button type="submit" className="primary-cta compact-button" disabled={posting || !commentDraft.trim()}>
                          Post Comment
                        </button>
                      </form>
                      ) : (
                        <p className="muted-text">Upgrade to VixPremium to join the discussion.</p>
                      )}
                    </div>
                  </>
                ) : null}
              </section>
            </main>
          </section>
        )}
      </div>
    </HelmetProvider>
  );
}

export default CommunityPage;
